import { useState, useEffect } from "react";
import { Wallet, Loader2, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import { Modal } from "./SharedComponents";
import { UNITS } from "./constants";

/* Fix GC (2026-10-14): "Compra desde caja chica: asignar los ítems con cantidades, UM, precios
   unitarios y montos editables; las cantidades parciales se mantienen en el análisis (como en la
   verificación de stock); genera una línea en Caja Chica con proveedor, cantidades, UM, PU,
   montos, ítems del presupuesto y tareas vinculados" (CEN). Lo elegido sale del análisis y
   nace una solicitud en Compras → Caja Chica, que aprueban Gerencia Financiera, Gerencia
   Técnica o Dirección (APROB / RECH / PAGAR). Los precios se cargan con IVA incluido. */
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const r4 = (n) => Math.round((Number(n) || 0) * 10000) / 10000;

export function CompraCajaChicaAPModal({ act, items, cerrados, open, onClose, onGuardarAntes, onListo }) {
  const [filas, setFilas] = useState([]);
  const [proveedor, setProveedor] = useState("");
  const [solicitante, setSolicitante] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [hecho, setHecho] = useState(null);
  const [origen, setOrigen] = useState(null);
  const pmId = act?.pm_origen_id || "";

  useEffect(() => {
    if (!open) return;
    setFilas((items || []).map(() => ({ sel: false, cantidad: "", unidad: "", pu: "", total: "" })));
    setProveedor(""); setSolicitante(""); setObservaciones(""); setHecho(null); setOrigen(null);
  }, [open, items]);

  useEffect(() => {
    if (!open || !pmId) return undefined;
    let vivo = true;
    axios.get(`${API}/purchases/pedido-origen/${pmId}`, { withCredentials: true })
      .then(({ data }) => {
        if (!vivo) return;
        setOrigen(data);
        setSolicitante(prev => prev || data.requested_by_name || "");
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [open, pmId]);

  if (!open || !act) return null;
  // Lo adjudicado a un proveedor ya cerrado queda con ese proveedor (como en Verificar stock).
  const enCerrados = (it) => (it.allocations || []).reduce((s, a) => s + ((cerrados || []).includes(a.provider_idx) ? Number(a.quantity) || 0 : 0), 0);
  const libre = (it) => Math.max(0, r4((Number(it?.quantity) || 0) - enCerrados(it || {})));
  // Precio propuesto: el menor P.U. cotizado (editable).
  const precioSugerido = (it) => {
    const ps = (it.prices || []).map(p => Number(p?.unit_price) || 0).filter(p => p > 0);
    return ps.length ? Math.min(...ps) : "";
  };
  const set = (i, patch) => setFilas(prev => prev.map((f, k) => (k === i ? { ...f, ...patch } : f)));

  const elegir = (i, on) => {
    const it = items[i];
    if (!on) return set(i, { sel: false });
    const q = libre(it);
    const pu = precioSugerido(it);
    set(i, { sel: true, cantidad: String(q), unidad: it.unit || "", pu: pu === "" ? "" : String(pu), total: pu === "" ? "" : String(r2(q * pu)) });
  };
  // Cantidad o P.U. recalculan el monto; el monto recalcula el P.U. (los tres se pueden corregir).
  const cambiar = (i, campo, valor) => {
    const f = filas[i];
    const n = { ...f, [campo]: valor };
    const q = Number(n.cantidad) || 0;
    if (campo === "total") {
      n.pu = q > 0 && Number(valor) > 0 ? String(r2(Number(valor) / q)) : n.pu;
    } else if (campo === "cantidad" || campo === "pu") {
      n.total = q > 0 && Number(n.pu) > 0 ? String(r2(q * Number(n.pu))) : n.total;
    }
    set(i, n);
  };

  const elegidas = filas.map((f, i) => ({ f, i })).filter(x => x.f.sel);
  const filaValida = ({ f, i }) => {
    const q = Number(f.cantidad) || 0;
    return q > 0 && q <= libre(items[i]) + 0.0001 && Number(f.total) > 0;
  };
  const totalCompra = r2(elegidas.reduce((s, x) => s + (Number(x.f.total) || 0), 0));
  const listo = elegidas.length > 0 && elegidas.every(filaValida) && proveedor.trim().length > 0;

  const confirmar = async () => {
    setEnviando(true);
    try {
      await onGuardarAntes();
      const { data } = await axios.post(`${API}/purchases/budget-acts/${act._id}/compra-caja-chica`, {
        proveedor: proveedor.trim(), solicitante: solicitante.trim(), observaciones: observaciones.trim(),
        lineas: elegidas.map(({ f, i }) => ({ item_idx: i, quantity: Number(f.cantidad), unit: f.unidad,
          unit_price: Number(f.pu) || 0, total: Number(f.total) })),
      }, { withCredentials: true });
      setHecho(data.petty_cash);
      onListo(data.act);
      toast.success(`Solicitud de Caja Chica: ${data.petty_cash.numero}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo generar la solicitud de Caja Chica");
    } finally {
      setEnviando(false);
    }
  };

  const inp = "w-full border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-amber-300 disabled:bg-gray-50 disabled:text-gray-300";

  return (
    <Modal open={open} onClose={() => !enviando && onClose()} title={hecho ? `Caja Chica solicitada · ${act.number}` : `Compra desde Caja Chica · ${act.number}`} extraWide>
      {hecho ? (
        <div className="space-y-4" data-testid="ap-cc-done">
          <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <CheckCircle2 size={20} className="text-emerald-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-800">
              Se generó la solicitud <strong>{hecho.numero}</strong> por <strong>{formatPYG(hecho.total_with_iva)}</strong> ({(hecho.items || []).length} línea{(hecho.items || []).length !== 1 ? "s" : ""}) a {hecho.provider}.
              {act.cubierto_por_caja_chica ? " Todo el pedido sale por Caja Chica: el análisis quedó cerrado." : " Las cantidades ya se descontaron del análisis; lo que no se compra por Caja Chica sigue para cotizar."}{" "}
              Queda <strong>Solicitada</strong> en Compras → Caja Chica hasta que Gerencia Financiera, Gerencia Técnica o Dirección la aprueben y la paguen; el comprobante se carga una vez pagada.
            </p>
          </div>
          <button type="button" onClick={onClose}
            className="w-full bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold" data-testid="ap-cc-cerrar">
            Cerrar
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            Marcá los ítems que se compran por Caja Chica y ajustá cantidad, UM, precio unitario (con IVA incluido) o monto. Lo que elijas <strong>se descuenta del análisis</strong>; si comprás una parte, el resto sigue en el análisis. Se genera una solicitud en Caja Chica con los ítems del presupuesto y las tareas ya vinculados.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">Proveedor *</label>
              <input value={proveedor} onChange={e => setProveedor(e.target.value)} list="ap-cc-proveedores" placeholder="Comercio donde se compra"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" data-testid="ap-cc-proveedor" />
              <datalist id="ap-cc-proveedores">{(act.provider_columns || []).map((p, k) => <option key={k} value={p} />)}</datalist>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">Solicitante</label>
              <input value={solicitante} onChange={e => setSolicitante(e.target.value)} placeholder="Quien pide (si no, vos)"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" data-testid="ap-cc-solicitante" />
              {origen?.requested_by_name && <p className="text-[10px] text-gray-400 mt-0.5" data-testid="ap-cc-solicitante-pm">Del pedido {origen.number}</p>}
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">Observaciones</label>
              <input value={observaciones} onChange={e => setObservaciones(e.target.value)} placeholder="Opcional"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" data-testid="ap-cc-observaciones" />
            </div>
          </div>
          {origen?.obra_nombre && (
            <p className="text-xs text-gray-600" data-testid="ap-cc-obra-pm">Obra del pedido {origen.number}: <strong>{origen.obra_nombre}</strong></p>
          )}
          <div className="border border-gray-200 rounded-xl overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr className="text-gray-500 uppercase text-[10px]">
                  <th className="py-2 px-2 w-8"></th>
                  <th className="py-2 px-2 text-left">Descripción</th>
                  <th className="py-2 px-2 text-left w-20">En análisis</th>
                  <th className="py-2 px-2 text-left w-24">Cant. a comprar</th>
                  <th className="py-2 px-2 text-left w-24">UM</th>
                  <th className="py-2 px-2 text-left w-28">P.U. con IVA</th>
                  <th className="py-2 px-2 text-left w-32">Monto con IVA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(items || []).map((it, i) => {
                  const f = filas[i] || { sel: false, cantidad: "", unidad: "", pu: "", total: "" };
                  const tope = libre(it);
                  const excede = f.sel && Number(f.cantidad) > tope + 0.0001;
                  return (
                    <tr key={i} className={f.sel ? "bg-amber-50/40" : ""} data-testid={`ap-cc-row-${i}`}>
                      <td className="py-2 px-2 align-top">
                        <input type="checkbox" checked={!!f.sel} disabled={tope <= 0} onChange={e => elegir(i, e.target.checked)} data-testid={`ap-cc-sel-${i}`} />
                      </td>
                      <td className="py-2 px-2 font-medium text-gray-800 align-top">{it.item_name}</td>
                      <td className="py-2 px-2 text-gray-700 align-top">
                        {it.quantity} {it.unit}
                        {enCerrados(it) > 0 && (
                          <p className="text-[10px] text-gray-500 leading-tight" data-testid={`ap-cc-${i}-cerrado`}>{enCerrados(it)} con proveedor cerrado · pueden salir {tope}</p>
                        )}
                      </td>
                      <td className="py-2 px-2 align-top">
                        <input type="number" min="0" step="any" disabled={!f.sel} value={f.cantidad} onChange={e => cambiar(i, "cantidad", e.target.value)}
                          className={`${inp} ${excede ? "border-red-400 bg-red-50" : ""}`} data-testid={`ap-cc-cant-${i}`} />
                        {excede && <p className="text-[10px] text-red-600 mt-0.5">Máx. {tope}</p>}
                      </td>
                      <td className="py-2 px-2 align-top">
                        <input list="ap-cc-um" disabled={!f.sel} value={f.unidad} onChange={e => set(i, { unidad: e.target.value })} className={inp} data-testid={`ap-cc-um-${i}`} />
                      </td>
                      <td className="py-2 px-2 align-top">
                        <input type="number" min="0" step="any" disabled={!f.sel} value={f.pu} onChange={e => cambiar(i, "pu", e.target.value)} className={inp} data-testid={`ap-cc-pu-${i}`} />
                      </td>
                      <td className="py-2 px-2 align-top">
                        <input type="number" min="0" step="any" disabled={!f.sel} value={f.total} onChange={e => cambiar(i, "total", e.target.value)} className={inp} data-testid={`ap-cc-total-${i}`} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <datalist id="ap-cc-um">{UNITS.map(u => <option key={u} value={u} />)}</datalist>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">{elegidas.length} ítem{elegidas.length !== 1 ? "s" : ""} elegido{elegidas.length !== 1 ? "s" : ""}</span>
            <span className="font-bold text-gray-900" data-testid="ap-cc-total-general">Total a Caja Chica: {formatPYG(totalCompra)}</span>
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} disabled={enviando}
              className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium disabled:opacity-60">
              Cancelar
            </button>
            <button type="button" onClick={confirmar} disabled={enviando || !listo}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="ap-cc-confirmar">
              {enviando ? <Loader2 size={15} className="animate-spin" /> : <Wallet size={15} />}
              {enviando ? "Procesando..." : "Generar solicitud de Caja Chica"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
